import { FunctionComponent, useEffect, useRef, useState } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { timestampOr } from "@/utils/generationTiming";

import {
	copywritingOrchestrationSelector,
	hydrateGenerationPhase,
	resetGeneration,
	setFrameworkOverrides,
	setFreshStartOverride,
	setGenerationError,
	setGenerationResult,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { CopywritingChannel, CopywritingStatus } from "./types";
import { useGenerateBarSubmit } from "./useGenerateBarSubmit";
import { sequenceToDraftItems } from "./sequenceToDraftItems.util";
import { BatchSettingsPanel } from "./BatchSettingsPanel";
import { FrameworkEffectivenessChip } from "./FrameworkEffectivenessChip";
import { FrameworksRow } from "./FrameworksRow";
import { GenerationStatus } from "./GenerationStatus";
import { GenerateBarToggles } from "./GenerateBarToggles";
import { GenerateBarInstructionRow } from "./GenerateBarInstructionRow";
import { GenerateBarControlRow } from "./GenerateBarControlRow";
import { PinnedFactsRow } from "./PinnedFactsRow";
import { SelectedSignalChip } from "./SelectedSignalChip";
import { useGenerateBarPrefill } from "./useGenerateBarPrefill";
import { useInstructionHistory } from "./useInstructionHistory";
import { useSenderChannelSelection } from "./useSenderChannelSelection";
import { useSignalChipState } from "./useSignalChipState";
import { useLinkedinInviteNote } from "./useLinkedinInviteNote";
import { GenerateBarModals } from "./GenerateBarModals";
import { GenerateBarProps } from "./GenerateBar.types";

// Re-exported for backward compatibility with external imports.
export type { AiDraftItem } from "./types";

export const GenerateBar: FunctionComponent<GenerateBarProps> = ({
	listId,
	prospectId,
	upcomingLeadIds = [],
	triggerDescription,
	seedSignalId,
	hasConversations,
	personaCategory,
	companyCategory,
	lastSenderUserId,
	isLastSenderConnected,
	connectedUserIds,
	pendingInvitationUserIds,
	connectionCheckDone,
	prospectFirstName,
	prospectHasEmail,
	prospectHasLinkedinUrl,
	prospectNotResolved,
	copywritingStatus,
	copywritingOutput,
	copywritingError,
	copywritingSince,
	onGenerationComplete,
	onSenderChannelChange,
	initialSenderUserId,
	initialChannel,
	portalContainer,
	compact,
	hideBatchMode,
	smallGenerate,
	bulkSelection,
	onBulkGenerated,
}) => {
	const dispatch = useAppDispatch();

	const {
		isGenerating,
		currentStage,
		currentStageLabel,
		error,
		freshStartOverride,
		frameworkOverrides,
		generationResult,
		alreadyInProgress,
		generationPhase,
		phaseSince,
		errorIsCredits,
	} = useAppSelector(copywritingOrchestrationSelector);

	// Autocomplete history for the "Tell AI what to write" instruction (localStorage).
	const { history: instructionHistory, addInstruction } = useInstructionHistory();

	// ── Sender + channel selection (state + auto-select / availability / notify effects) ──
	const {
		availableUsers,
		senderOptions,
		selectedSendingUser,
		setSelectedSendingUser,
		selectedChannel,
		setSelectedChannel,
		availableChannels,
	} = useSenderChannelSelection({
		initialSenderUserId,
		initialChannel,
		prospectHasEmail,
		prospectHasLinkedinUrl,
		connectedUserIds,
		connectionCheckDone,
		leadKey: prospectId,
		onSenderChannelChange,
	});

	// ── "Add note to LinkedIn invite" toggle (connection + LinkedIn-tier rules) ──
	const {
		linkedinInviteNote,
		setLinkedinInviteNote,
		linkedinInviteNoteDisabled,
		linkedinInviteNoteTooltip,
	} = useLinkedinInviteNote({
		prospectId,
		hasConversations,
		lastSenderUserId,
		isLastSenderConnected,
		selectedSendingUser,
		connectedUserIds,
		pendingInvitationUserIds,
		prospectFirstName,
	});

	// ── Local state ──
	// The instruction box is the user's own directive only — the signal is shown separately as a
	// removable chip (below), not pre-filled here (PREC-3710).
	const [userInstruction, setUserInstruction] = useState<string>("");
	const channel = CopywritingChannel.BOTH;
	const [language, setLanguage] = useState<string>("auto");
	const [batchMode, setBatchMode] = useState(false);
	const [batchSize, setBatchSize] = useState(5);
	const [trigger, setTrigger] = useState("auto");
	const [senderMode, setSenderMode] = useState<"randomized" | "specific">("specific");
	const [showLanguageRulesModal, setShowLanguageRulesModal] = useState(false);
	const [showFrameworksModal, setShowFrameworksModal] = useState(false);
	const { signalDismissed, dismissSignal, showSignalChip, signalActive } = useSignalChipState({
		prospectId,
		seedSignalId,
		triggerDescription,
		batchMode,
		isBulk: !!bulkSelection,
	});

	// Track the last delivered result to avoid re-delivering stale results.
	// Tracks every result reference we've already delivered (or that we should
	// never deliver). Seeded with the slice's current `generationResult` on mount
	// so re-opening the panel for the same prospect doesn't immediately re-fire
	// `onGenerationComplete` with the leftover result from a previous session.
	// The hydration effect below adds `copywritingOutput` to this set when it
	// pre-seeds the slice, so that the eventual re-render with the slice value
	// equal to `copywritingOutput` is also skipped.
	// No cleanup needed on prospectId change — resetGeneration() clears the
	// slice and new generations always produce fresh object references.
	const deliveredResultsRef = useRef<Set<unknown>>(new Set([generationResult]));

	// Gate the hydration effect to fire at most once per prospectId. Subsequent
	// changes to copywritingStatus / copywritingOutput driven by the live socket
	// must not re-seed `deliveredResultsRef`, otherwise a freshly arrived result
	// would be silently swallowed by the conversion effect below.
	const hydratedForProspectRef = useRef<number | null>(null);

	// Reset generation state on mount (panel open) and when prospect changes,
	// then hydrate from persisted `list_person` copywriting fields so the UI
	// reflects the last run / an ongoing run when the panel is re-opened.
	useEffect(() => {
		if (hydratedForProspectRef.current === prospectId) {
			return;
		}

		hydratedForProspectRef.current = prospectId;

		dispatch(resetGeneration());

		if (copywritingStatus === CopywritingStatus.COMPLETED && copywritingOutput) {
			// Pre-seed the delivered-results set so the conversion effect does not
			// re-deliver drafts that were already prefilled from the persisted
			// `list_person.copywritingOutput` by the parent (useAddInboxChat).
			deliveredResultsRef.current.add(copywritingOutput);
			dispatch(setGenerationResult(copywritingOutput));

			return;
		}

		if (
			copywritingStatus === CopywritingStatus.QUEUED ||
			copywritingStatus === CopywritingStatus.RUNNING
		) {
			dispatch(
				hydrateGenerationPhase({
					phase: copywritingStatus === CopywritingStatus.QUEUED ? "queued" : "running",
					since: timestampOr(copywritingSince, Date.now()),
				})
			);

			return;
		}

		if (copywritingStatus === CopywritingStatus.FAILED && copywritingError) {
			dispatch(setGenerationError(copywritingError));
		}
	}, [
		prospectId,
		dispatch,
		copywritingStatus,
		copywritingOutput,
		copywritingError,
		copywritingSince,
	]);

	// ── Prefill from the list's Outreach step (PREC-3687 D8 / AC 18) ──
	// Called AFTER the hydration effect above on purpose: that effect dispatches `resetGeneration()`,
	// which clears `frameworkOverrides`, and React runs effects in hook order — a framework seeded
	// before it would be wiped a tick later.
	const { selectLanguage, keepUserFrameworks } = useGenerateBarPrefill({
		listId,
		prospectId,
		setLanguage,
	});

	// The copywriting socket is owned at page level by `useFeedDraftGeneration`
	// (so live progress also works on the Feed tab where GenerateBar is unmounted).
	// GenerateBar reads `isGenerating` / `generationResult` from the slice, which
	// that socket keeps updated.

	// ── Convert AI result to drafts when generation completes ──
	useEffect(() => {
		if (!generationResult || generationResult.type !== "full_sequence" || !onGenerationComplete) {
			return;
		}

		// Skip if this result was already delivered (or was pre-seeded as
		// "do-not-deliver" by the mount-time / hydration logic above).
		if (deliveredResultsRef.current.has(generationResult)) {
			return;
		}

		const draftItems = sequenceToDraftItems(
			generationResult,
			channel,
			generationResult.contextPayload,
			generationResult.messagingStrategy
		);

		if (draftItems.length > 0) {
			deliveredResultsRef.current.add(generationResult);
			onGenerationComplete(draftItems, generationResult.contextPayload);
		}
	}, [generationResult, channel, onGenerationComplete]);

	// ── Submit ("Generate") — single lead / next-N batch / bulk selection (see the hook) ──
	const { handleGenerate, creditCount } = useGenerateBarSubmit({
		prospectId,
		listId,
		channel,
		language,
		batchMode,
		batchSize,
		upcomingLeadIds,
		senderMode,
		selectedSendingUser,
		availableUsers,
		userInstruction,
		linkedinInviteNote,
		frameworkOverrides,
		freshStartOverride,
		addInstruction,
		bulkSelection,
		onBulkGenerated,
		seedSignalId: signalActive ? seedSignalId : undefined,
		ignoreSignal: !!seedSignalId && signalDismissed,
	});

	return (
		<div className="z-[100] flex flex-col gap-3 w-full">
			{/* ── Main card ── */}
			<div className="rounded-xl border bg-white border-border-light p-4 flex flex-col gap-3">
				{/* Row 1: the "Tell AI what to write" instruction input */}
				<GenerateBarInstructionRow
					compact={compact}
					history={instructionHistory}
					isGenerating={isGenerating}
					portalContainer={portalContainer}
					userInstruction={userInstruction}
					onChangeInstruction={setUserInstruction}
				/>

				{/* Selected signal (removable) — seeds the generation's trigger; remove to write without it */}
				{/* Research facts pinned for this lead — the sequence must mention them
				    (PREC-3680). Hidden for a bulk selection: pins belong to one prospect, so
				    the seed row's pins say nothing about the other leads in the batch. */}
				{!bulkSelection && <PinnedFactsRow disabled={isGenerating} prospectId={prospectId} />}

				{showSignalChip && (
					<SelectedSignalChip
						description={triggerDescription ?? ""}
						disabled={isGenerating}
						onRemove={dismissSignal}
					/>
				)}

				{/* Row 2: language + sender + channel (left) + generate button (right), single mode only */}
				{!batchMode && (
					<GenerateBarControlRow
						alreadyInProgress={alreadyInProgress}
						availableChannels={availableChannels}
						compact={compact}
						connectedUserIds={connectedUserIds}
						connectionCheckDone={connectionCheckDone}
						generationPhase={generationPhase}
						pendingInvitationUserIds={pendingInvitationUserIds}
						isGenerating={isGenerating}
						language={language}
						linkedinInviteNote={linkedinInviteNote}
						portalContainer={portalContainer}
						prospectFirstName={prospectFirstName}
						prospectNotResolved={prospectNotResolved}
						selectedChannel={selectedChannel}
						selectedSendingUser={selectedSendingUser}
						senderOptions={senderOptions}
						setSelectedChannel={setSelectedChannel}
						smallGenerate={smallGenerate}
						setSelectedSendingUser={setSelectedSendingUser}
						onGenerate={handleGenerate}
						onOpenRules={() => setShowLanguageRulesModal(true)}
						onSelectLanguage={selectLanguage}
					/>
				)}

				{/* Row 2 (batch mode): generate button only */}
				{batchMode && (
					<div className="flex items-center gap-2 justify-end">
						<div className="w-fit">
							<Button
								color={ButtonColor.ACTION}
								disabled={
									isGenerating ||
									alreadyInProgress ||
									prospectNotResolved ||
									(!selectedSendingUser && senderMode === "specific")
								}
								image={<Icon className="mr-1" icon={faSparkles} />}
								isLoading={isGenerating || alreadyInProgress}
								size={ButtonSize.S}
								title={`Generate for ${batchSize} lead${batchSize > 1 ? "s" : ""}`}
								onClick={handleGenerate}
							/>
						</div>
					</div>
				)}

				<GenerateBarToggles
					batchMode={batchMode}
					batchSize={batchSize}
					creditCount={creditCount}
					hideBatchMode={(hideBatchMode ?? compact) || !!bulkSelection}
					isGenerating={isGenerating}
					linkedinInviteNote={linkedinInviteNote}
					linkedinInviteNoteDisabled={linkedinInviteNoteDisabled}
					linkedinInviteNoteTooltip={linkedinInviteNoteTooltip}
					setBatchMode={setBatchMode}
					setLinkedinInviteNote={setLinkedinInviteNote}
				/>

				<FrameworkEffectivenessChip
					companyCategory={companyCategory}
					frameworkOverrides={frameworkOverrides}
					personaCategory={personaCategory}
				/>

				{/* Frameworks row (single mode) — batch mode renders it inside the settings panel. */}
				{!batchMode && (
					<div className="pt-3 border-t border-border-light">
						<FrameworksRow
							compact={compact}
							frameworkOverrides={frameworkOverrides}
							isGenerating={isGenerating}
							onOpenFrameworksModal={() => setShowFrameworksModal(true)}
						/>
					</div>
				)}

				{/* Batch settings panel */}
				{batchMode && (
					<BatchSettingsPanel
						availableUsers={availableUsers}
						batchSize={batchSize}
						frameworkOverrides={frameworkOverrides}
						isGenerating={isGenerating}
						language={language}
						selectedSender={selectedSendingUser}
						senderMode={senderMode}
						setBatchSize={setBatchSize}
						setLanguage={selectLanguage}
						setSelectedSender={setSelectedSendingUser}
						setSenderMode={setSenderMode}
						setTrigger={setTrigger}
						trigger={trigger}
						onOpenFrameworksModal={() => setShowFrameworksModal(true)}
						onOpenLanguageModal={() => setShowLanguageRulesModal(true)}
					/>
				)}

				<GenerationStatus
					alreadyInProgress={alreadyInProgress}
					currentStage={currentStage}
					currentStageLabel={currentStageLabel}
					error={error}
					errorIsCredits={errorIsCredits}
					freshStartOverride={freshStartOverride}
					generationPhase={generationPhase}
					handleFreshStart={() => dispatch(setFreshStartOverride(!freshStartOverride))}
					hasConversations={hasConversations}
					isGenerating={isGenerating}
					phaseSince={phaseSince}
					onRetry={handleGenerate}
				/>
			</div>

			<GenerateBarModals
				companyCategory={companyCategory}
				frameworkOverrides={frameworkOverrides}
				personaCategory={personaCategory}
				showFrameworksModal={showFrameworksModal}
				showLanguageRulesModal={showLanguageRulesModal}
				onCloseFrameworks={() => setShowFrameworksModal(false)}
				onCloseLanguageRules={() => setShowLanguageRulesModal(false)}
				onSaveFrameworks={(overrides) => {
					keepUserFrameworks();
					dispatch(setFrameworkOverrides(overrides));
					setShowFrameworksModal(false);
				}}
			/>
		</div>
	);
};
